// /frontend/src/services/orderService.js
import apiClient from '../utils/apiClient';

const API_URL = `${process.env.REACT_APP_API_URL}/orders`;
const PAYMENT_URL = `${process.env.REACT_APP_API_URL}/payment`;

export const orderService = {
  // Get all orders
  getOrders: async (params = {}) => {
    try {
      const response = await apiClient.get(API_URL, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get order by ID
  getOrderById: async (id) => {
    try {
      const response = await apiClient.get(`${API_URL}/${id}`);
      console.log('Raw API response:', response); // Log the full response
      
      // Check the exact structure of your response
      console.log('Response data structure:', {
        data: response.data,
        hasData: !!response.data,
        hasOrder: response.data && !!response.data.data,
        orderProperties: response.data && response.data.data ? Object.keys(response.data.data) : []
      });
      
      // Make sure you're accessing the right property path
      const orderData = response.data.data || response.data;
      
      // Verify order data exists and has expected properties
      if (!orderData) {
        console.error('Order data is null or undefined');
        throw new Error('Order data not found in response');
      }
      
      console.log('Returning order data:', orderData);
      return orderData;
    } catch (error) {
      console.error('Error in getOrderById:', error);
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Create new order
  createOrder: async (orderData) => {
    try {
      const response = await apiClient.post(API_URL, orderData);
      
      // Add debugging
      console.log('API response:', response.data);
      
      // Make sure this path matches your API response structure
      const order = response.data.data || response.data;

      // Verify order has expected properties before returning
      if (!order || !order.order._id) {
        console.error('API returned order without _id', order);
        throw new Error('Invalid order data returned from server');
      }
      
      return order;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Update order
  updateOrder: async (id, orderData) => {
    try {
      const response = await apiClient.put(`${API_URL}/${id}`, orderData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Update order status
  updateOrderStatus: async (id, statusData) => {
    try {
      const response = await apiClient.put(`${API_URL}/${id}/status`, statusData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Delete order
  deleteOrder: async (id) => {
    try {
      const response = await apiClient.delete(`${API_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get order history
  getOrderHistory: async (id) => {
    try {
      const response = await apiClient.get(`${API_URL}/${id}/history`);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get my orders (for logged in user)
  getMyOrders: async () => {
    try {
      const response = await apiClient.get(`${API_URL}/myorders`);
      return response.data.orders;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Cancel order
  cancelOrder: async (id) => {
    try {
      const response = await apiClient.put(`${API_URL}/${id}/cancel`);
      return response.data.order;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Process payment
  processPayment: async (orderId, paymentMethod, paymentDetails) => {
    try {
      const response = await apiClient.post(`${PAYMENT_URL}/process`, {
        orderId,
        paymentMethod,
        paymentDetails
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Update order to paid
  updateOrderToPaid: async (id, paymentResult) => {
    try {
      const response = await apiClient.put(`${API_URL}/${id}/pay`, paymentResult);
      return response.data.order;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Get pending payments (admin only)
  getPendingPayments: async () => {
    try {
      const response = await apiClient.get(`${PAYMENT_URL}/pending`);
      return response.data.payments;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Confirm payment (admin only)
  confirmPayment: async (orderId, data) => {
    try {
      const response = await apiClient.put(`${PAYMENT_URL}/confirm/${orderId}`, data);
      return response.data.order;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Reject payment (admin only)
  rejectPayment: async (orderId, reason) => {
    try {
      const response = await apiClient.put(`${PAYMENT_URL}/reject/${orderId}`, { reason });
      return response.data.order;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
  
  // Save shipping address to localStorage
  saveShippingAddress: (shippingAddress) => {
    localStorage.setItem('shippingAddress', JSON.stringify(shippingAddress));
  },
  
  // Get shipping address from localStorage
  getShippingAddress: () => {
    const shippingAddress = localStorage.getItem('shippingAddress');
    return shippingAddress ? JSON.parse(shippingAddress) : null;
  },
  
  // Save payment method to localStorage
  savePaymentMethod: (paymentMethod) => {
    localStorage.setItem('paymentMethod', paymentMethod);
  },
  
  // Get payment method from localStorage
  getPaymentMethod: () => {
    return localStorage.getItem('paymentMethod');
  },
  
  // Save payment details to localStorage (non-sensitive info only)
  savePaymentDetails: (paymentDetails) => {
    // Remove any sensitive data before storing
    const safeDetails = { ...paymentDetails };
    delete safeDetails.cvv;
    
    localStorage.setItem('paymentDetails', JSON.stringify(safeDetails));
  },
  
  // Get payment details from localStorage
  getPaymentDetails: () => {
    const paymentDetails = localStorage.getItem('paymentDetails');
    return paymentDetails ? JSON.parse(paymentDetails) : null;
  },
  
  // Clear checkout data from localStorage
  clearCheckoutData: () => {
    localStorage.removeItem('shippingAddress');
    localStorage.removeItem('paymentMethod');
    localStorage.removeItem('paymentDetails');
  },

  // Update payment method
  updatePaymentMethod: async (orderId, paymentInfo) => {
    try {
      const response = await apiClient.put(
        `${API_URL}/${orderId}/payment-method`,
        paymentInfo
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },

  // Upload payment evidence
  uploadPaymentEvidence: async (orderId, formData, progressCallback) => {
    try {
      const response = await apiClient.post(
        `${API_URL}/${orderId}/payment-evidence`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            if (progressCallback) {
              progressCallback(percentCompleted);
            }
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },

  // Verify payment (admin only)
  verifyPayment: async (orderId, data) => {
    try {
      const response = await apiClient.put(
        `${API_URL}/${orderId}/verify-payment`,
        data
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || error.message;
    }
  },
};